import axios from "axios";

export const api = axios.create({
    // baseURL: "http://127.0.0.1:8000"
    // baseURL: "https://test.api.mealno.in"
    baseURL: "https://api.mealno.in"
});

// 1. REQUEST INTERCEPTOR: Attach the Access Token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("vendor_access_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ==========================================
// MUTEX & QUEUE VARIABLES FOR CONCURRENT 401s
// ==========================================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// 2. RESPONSE INTERCEPTOR: Handle 401s and Auto-Refresh
api.interceptors.response.use(
    (response) => response, // If response is successful, just return it
    async (error) => {
        const originalRequest = error.config;

        // If error is 401, and we haven't already retried this exact request
        if (error.response?.status === 401 && !originalRequest._retry) {
            
            // Do not intercept if the request failing WAS the login or refresh route itself
            if (originalRequest.url === '/auth/refresh' || originalRequest.url === '/vendor/login') {
                return Promise.reject(error);
            }

            // If another request is already refreshing the token, wait in the queue
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return api(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            // Lock the refresh process
            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = localStorage.getItem("vendor_refresh_token");

            if (!refreshToken) {
                isRefreshing = false;
                processQueue(new Error("No refresh token available"), null);
                return Promise.reject(error);
            }

            try {
                // Call the backend to refresh the token using standard axios
                const res = await axios.post(`${api.defaults.baseURL}/auth/refresh`, {
                    refresh_token: refreshToken
                });

                const newAccessToken = res.data.access_token;

                // Save the newly minted access token
                localStorage.setItem("vendor_access_token", newAccessToken);

                // Update the failed request with the fresh token
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

                // Release the queue and process waiting requests
                processQueue(null, newAccessToken);
                
                // Re-run the original failed request seamlessly!
                return api(originalRequest);

            } catch (refreshError) {
                const status = refreshError.response?.status;
                
                // Reject all queued requests
                processQueue(refreshError, null);

                // Only wipe the session on a genuine 401 from /auth/refresh
                if (status === 401) {
                    localStorage.removeItem("vendor_access_token");
                    localStorage.removeItem("vendor_refresh_token");
                    localStorage.removeItem("vendor");
                    localStorage.removeItem("vendor_id");

                    window.location.href = "/login";
                }

                return Promise.reject(refreshError);
            } finally {
                // Unlock the refresh process regardless of success or failure
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);